import React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import styled from 'src/ui/library/styled';

const styles = theme => ({
  tabsRoot: {
    paddingLeft: '1.5rem',
    backgroundColor: theme.colors.eyConfidentGrey,
  },
  tabsIndicator: {
    backgroundColor: theme.colors.eyYellow,
    borderBottom: `1px solid ${theme.colors.eyYellow}`,
  },
  tabRoot: {
    color: theme.colors.eyGrey6,
    textTransform: 'initial',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    // padding: '0 1.25rem',
    backgroundColor: theme.colors.eyGrey0,
    fontSize: '14px',
    padding: '6px 30px',
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&$tabSelected': {
      color: theme.colors.eyGrey4,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: theme.colors.eyInteractiveOverlays,
    },
    '&:focus': {
      color: theme.colors.white,
    },
  },
  tabSelected: {},
  label: {
    fontSize: '14px',
  },
  typography: {
    padding: theme.spacing(3),
  },
  allCases: {
    backgroundColor: '#1777CF',
  },
  openCases: {
    backgroundColor: '#2DB757',
  },
  closedCases: {
    backgroundColor: '#FF4136',
  },
});

class TabBase extends React.Component {
  config = {
    indicatorColors: [],
  };
  state = {
    value: this.props.defaultValue !== undefined ? this.props.defaultValue : this.props.defaultTab || 0,
    tabClick: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    /* This code is needed so that the tabs can accept a value
     * from an outside routing, not just by clicking the tab */
    return {
      tabClick: false,
      value: nextProps.tabIdx !== undefined && !prevState.tabClick ? nextProps.tabIdx : prevState.value,
    };
  }

  handleChange = (event, value) => {
    let { handleChangeStatus } = this.props;

    this.setState({ value, tabClick: true }, () => {
      handleChangeStatus(this.state.value);
    });
  };

  handleTabClick = (event, value) => {
    const { defaultValue, handleChangeStatus, onClick, tabIdx } = this.props;
    const selectedValue = defaultValue !== undefined ? defaultValue : tabIdx !== undefined ? tabIdx : this.state.value;

    if (value === selectedValue && typeof handleChangeStatus === 'function') {
      handleChangeStatus(value);
    }

    if (typeof onClick === 'function') {
      onClick(event, value);
    }
  };

  generateTabs = (props, otherProps) => {
    let { classes, labels, openCaseCount, closedCaseCount, allCaseCount, styleType } = props;
    let casesCount = 0;
    // clear indicatorColors and push all new colors for tabIndicators
    this.config.indicatorColors.length = 0;
    return labels.map((label, index) => {
      let colorType = 'primary';
      let labelType = label.toLowerCase();
      if (allCaseCount && labelType === 'all cases') {
        colorType = 'primary';
        casesCount = allCaseCount;
        this.config.indicatorColors.push(classes.allCasesIndicator);
      } else if (openCaseCount && labelType === 'open cases') {
        colorType = 'secondary';
        casesCount = openCaseCount;
        this.config.indicatorColors.push(classes.openCasesIndicator);
      } else if (closedCaseCount && labelType === 'closed cases') {
        colorType = 'error';
        casesCount = closedCaseCount;
        this.config.indicatorColors.push(classes.closedCasesIndicator);
      } else {
        this.config.indicatorColors.push(classes.tabsIndicator);
        casesCount = 0;
      }

      return styleType === 'primary' ? (
        <Tab
          key={label + index}
          value={index}
          disableRipple
          classes={{ root: classes.tabRoot, selected: classes.tabSelected, label: classes.label }}
          label={label}
          onClick={event => this.handleTabClick(event, index)}
          {...otherProps}
        />
      ) : (
        <Tab
          key={label + index}
          disableRipple
          classes={{ root: classes.tabRoot, selected: classes.tabSelected, label: classes.label }}
          onClick={event => this.handleTabClick(event, index)}
          label={
            <Badge
              classes={{
                root: classes.badge,
                colorPrimary: classes.allCases,
                colorSecondary: classes.openCases,
                colorError: classes.closedCases,
              }}
              badgeContent={casesCount}
              color={colorType}>
              {label}
            </Badge>
          }
          {...otherProps}
        />
      );
    });
  };

  render() {
    const {
      classes,
      styleType,
      openCaseCount,
      closedCaseCount,
      allCaseCount,
      labels,
      children,
      defaultValue,
      ...otherProps
    } = this.props;
    const { value } = this.state;
    delete otherProps.handleChangeStatus;
    delete otherProps.defaultTab;
    delete otherProps.tabIdx;
    delete otherProps.onClick;

    let props = {
      styleType,
      openCaseCount,
      closedCaseCount,
      allCaseCount,
      classes,
      labels,
    };

    const val = defaultValue !== undefined ? defaultValue : value;
    const tabs = this.generateTabs(props, otherProps);

    return (
      <div className={classes.root}>
        <Tabs
          value={val}
          onChange={this.handleChange}
          classes={{ root: classes.tabsRoot, indicator: this.config.indicatorColors[val] }}
          {...otherProps}>
          {tabs}
        </Tabs>
        {children}
      </div>
    );
  }
}

TabBase.propTypes = {
  classes: PropTypes.object.isRequired,
  allCaseCount: PropTypes.number,
  children: PropTypes.node,
  closedCaseCount: PropTypes.number,
  defaultTab: PropTypes.any,
  defaultValue: PropTypes.any,
  handleChangeStatus: PropTypes.func,
  labels: PropTypes.array,
  openCaseCount: PropTypes.number,
  styleType: PropTypes.string,
  tabIdx: PropTypes.number,
};

export { TabBase };
export default styled(TabBase)(styles);
